import { useEffect, useMemo, useState } from "react";

import { type Option, type OptionValue } from "@/components/types/option";

import { exclude, getValue } from "../../SelectBase";

type SelectionMultipleProp<TValue extends Option> = {
  value: OptionValue<TValue>[] | undefined;
  disabledOptions?: OptionValue<TValue>[];
  options: TValue[];
  optionLabel: string;
  dataKey: string;
  optionValue?: string;
  onChange?: (value: OptionValue<TValue>[]) => void;
};

function useSelectionMultiple<TValue extends Option>({
  options,
  value,
  disabledOptions,
  onChange,
  dataKey,
  optionLabel,
  optionValue,
}: SelectionMultipleProp<TValue>) {
  const [selected, setSelected] = useState<OptionValue<TValue>[] | undefined>();
  useEffect(() => {
    setSelected(value);
  }, [value]);

  const { isAllSelected, isPartialSelected } = useMemo(() => {
    if (!selected || !Array.isArray(selected)) {
      return { isAllSelected: false, isPartialSelected: false };
    }
    return {
      isAllSelected: selected.length === options.length,
      isPartialSelected: selected.length > 0 && selected.length < options.length,
    };
  }, [selected, options]);

  const doSelect = (opt: TValue, checked: boolean) => {
    const newSelected = exclude(selected, opt, {
      optionLabel,
      dataKey,
    });
    if (checked) {
      newSelected.push(getValue(opt, optionValue));
    }
    setSelected(newSelected);
    onChange?.(newSelected);
  };

  const getEmptySelection = (): OptionValue<TValue>[] => {
    if (!disabledOptions || !selected) {
      return [];
    }

    // solo incluye las disabled que esten seleccionadas
    return disabledOptions.filter((x) => {
      const keyOfX = getValue(x, dataKey);
      return selected?.some((y) => getValue(y, dataKey) === keyOfX);
    });
  };

  const doToggleSelectAll = () => {
    const newSelection = isAllSelected ? getEmptySelection() : options;
    onChange?.(newSelection);
  };

  return {
    selected,
    isPartialSelected,
    isAllSelected,
    doToggleSelectAll,
    doSelect,
  };
}

export { useSelectionMultiple };
